// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Declare default config and serialization methods.
 * @author James Reid
 */

// @ts-check

// @@imports-utils
import { BitBuffer } from "./utils/index.js"

// @@imports-types
import {
    Config,
    MEMBRANES,
    JOIN_TYPES,
    SUBSTRATE_TYPES,
    TRIM_COLORS,
    TRIM_DEPTHS,
    TRIM_TYPES,
    UNIT_TYPES,
    SEAM_DIRECTIONS
} from "./types/index.js"

// @@body
// Please note that as with all other measurements in this calculator, all
// relevant values below are in SI units (i.e. metres, kg etc.) unless otherwise
// specified.
/** @type {Config} */
const epdmConfig = {
    adhesiveCoverages: {
        // Note that all deck area coverages consider covering *both* sides of
        // the membrane with the exception of pu adhesive, which has a single
        // sided coverage rate due to method of application.
        deck: {
            waterBased: {
                default: 4,
                min: 3,
                max: 4,
                client: 4,
                unitType: UNIT_TYPES.area
            },
            contactBonding: {
                default: 2,
                min: 1,
                max: 2,
                client: 2,
                unitType: UNIT_TYPES.area
            },
            pu: {
                default: 3.3,
                min: 2.8,
                max: 3.7,
                client: 3.3,
                unitType: UNIT_TYPES.area
            }
        },
        primer: {
            default: 4,
            min: 3.7,
            max: 6.6,
            client: 4,
            unitType: UNIT_TYPES.area
        },
        sealants: {
            lapEdge: {
                default: 18.2,
                min: 18.2,
                max: 18.2,
                client: 18.2,
                unitType: UNIT_TYPES.length
            },
            waterBlock: {
                default: 9.1,
                min: 9.1,
                max: 9.1,
                client: 9.1,
                unitType: UNIT_TYPES.length
            },
            msPolymer: {
                default: 1,
                min: 1,
                max: 1,
                client: 1,
                unitType: UNIT_TYPES.volume
            },
            blackSilicone: {
                default: 18.2,
                min: 18.2,
                max: 18.2,
                client: 18.2,
                unitType: UNIT_TYPES.length
            }
        }
    },
    clientLimits: {
        roll: {
            width: Infinity,
            weight: Infinity,
            waste: {
                upper: 1.125,
                lower: 0.125
            }
        },
        adhesive: {
            waste: {
                upper: 2.5,
                lower: 1
            }
        }
    },
    overhangs: {
        houseWall: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        parapetWall: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        gutterDrip: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        raisedKerb: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        pitchRoofOn: {
            default: 0.30,
            min: 0.30,
            client: 0.30
        },
        pitchRoofOff: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        feltJoin: {
            default: 0.15,
            min: 0.085,
            client: 0.15
        },
        seamJoin: {
            default: 0.15,
            min: 0.085,
            client: 0.15
        },
        buttJoin: {
            default: 0.15,
            min: 0,
            client: 0.15
        },
        fleeceBackedJoin: {
            default: 0.15,
            min: 0.085,
            client: 0.15
        },
        none: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        },
        default: {
            default: 0.15,
            min: 0.15,
            client: 0.15
        }
    },
    parapet: {
        width: 0.23,
        height: 0.5
    },
    membrane: MEMBRANES.onePiece_1_20,
    seamDirection: SEAM_DIRECTIONS.any,
    substrate: SUBSTRATE_TYPES.osb,
    rubberJoin: JOIN_TYPES.seam,
    contactBorderDepth: 0.15,
    tapes: {
        overlap: 0.075,
        primerBorder: 0.01,
        addSealant: false,
        precutPatches: true
    },
    extraAllowances: {
        tape: 1,
        adhesive: 1
    },
    trims: {
        type: TRIM_TYPES.cellular,
        color: TRIM_COLORS.black,
        depth: TRIM_DEPTHS.standard,
        minLength: 0,
        maxLength: 500
    }
}

/**
 * @typedef {"boolean"|"float"|"integer"|"option"} SerialConfigEntryType
 */

/** @enum {SerialConfigEntryType} */
export const SERIAL_CONFIG_ENTRY_TYPES = {
    boolean: /** @type {SerialConfigEntryType} */ ("boolean"),
    float: /** @type {SerialConfigEntryType} */ ("float"),
    integer: /** @type {SerialConfigEntryType} */ ("integer"),
    option: /** @type {SerialConfigEntryType} */ ("option")
}

/**
 * @typedef SerialConfigEntry
 * @property {string} prop
 * @property {SerialConfigEntryType} [type]
 * @property {any[]} [options]
 */

/**
 * @typedef {SerialConfigEntry[]|SerialConfigEntry[][]|SerialConfigEntry[][][]|SerialConfigEntry[][][][]} SerialConfigOrder // @no-wrap
 */

// 
/** @type {SerialConfigOrder} */
const epdmConfigSerialOrder = [
    // Adhesive coverage config.
    [
        { prop: "adhesiveCoverages.deck.waterBased.client" },
        { prop: "adhesiveCoverages.deck.contactBonding.client" },
        { prop: "adhesiveCoverages.deck.pu.client" },
        { prop: "adhesiveCoverages.primer.client" },
        { prop: "adhesiveCoverages.sealants.lapEdge.client" },
        { prop: "adhesiveCoverages.sealants.waterBlock.client" },
        { prop: "adhesiveCoverages.sealants.msPolymer.client" },
        { prop: "adhesiveCoverages.sealants.blackSilicone.client" }
    ],

    // Dimension limits for bill of material items.
    [
        { prop: "clientLimits.roll.width" },
        { prop: "clientLimits.roll.weight" }
    ],

    [
        { prop: "extraAllowances.tape" },
        { prop: "extraAllowances.adhesive" }
    ],

    [
        { prop: "overhangs.houseWall.client" },
        { prop: "overhangs.parapetWall.client" },
        { prop: "overhangs.gutterDrip.client" },
        { prop: "overhangs.raisedKerb.client" },
        { prop: "overhangs.pitchRoofOn.client" },
        { prop: "overhangs.pitchRoofOff.client" },
        { prop: "overhangs.feltJoin.client" },
        { prop: "overhangs.seamJoin.client" },
        { prop: "overhangs.buttJoin.client" },
        { prop: "overhangs.fleeceBackedJoin.client" },
        { prop: "overhangs.none.client" },
        { prop: "overhangs.default.client" }
    ],

    [
        { prop: "parapet.width" },
        { prop: "parapet.height" }
    ],

    [
        {
            prop: "membrane",
            type: SERIAL_CONFIG_ENTRY_TYPES.option,
            options: Object.values(MEMBRANES)
        },
        {
            prop: "seamDirection",
            type: SERIAL_CONFIG_ENTRY_TYPES.option,
            options: Object.values(SEAM_DIRECTIONS)
        },
        {
            prop: "substrate",
            type: SERIAL_CONFIG_ENTRY_TYPES.option,
            options: Object.values(SUBSTRATE_TYPES)
        },
        {
            prop: "rubberJoin",
            type: SERIAL_CONFIG_ENTRY_TYPES.option,
            options: Object.values(JOIN_TYPES)
        },
        { prop: "contactBorderDepth" }
    ],

    [
        { prop: "tapes.overlap" },
        { prop: "tapes.primerBorder" },
        { prop: "addSealant", type: SERIAL_CONFIG_ENTRY_TYPES.boolean },
        { prop: "precutPatches", type: SERIAL_CONFIG_ENTRY_TYPES.boolean }
    ],

    [
        {
            prop: "trims.type",
            type: SERIAL_CONFIG_ENTRY_TYPES.option,
            options: Object.values(TRIM_TYPES)
        },
        {
            prop: "trims.color",
            type: SERIAL_CONFIG_ENTRY_TYPES.option,
            options: Object.values(TRIM_COLORS)
        },
        {
            prop: "trims.depth",
            type: SERIAL_CONFIG_ENTRY_TYPES.option,
            options: Object.values(TRIM_DEPTHS)
        },
        { prop: "trims.minLength" },
        { prop: "trims.maxLength" }
    ]
]

/**
 * 
 * @param {Config} config 
 * @param {SerialConfigOrder} order
 * @param {object} obj
 * @param {number} [obj.magnitude] 
 * @param {number} [obj.significantFigures] 
 * @param {number} [obj.maxOptions]
 * @param {string[]} [obj.separators]
 * @param {boolean} [obj.includeDataSizeString]
 * @returns {string}
 */
const serializeConfig = (config, order, {
    magnitude = 4,
    significantFigures = 5,
    maxOptions = 31,
    separators = ["+", "!", "$"],
    includeDataSizeString = true
} = {}) => {
    // 
    const dataSizeString = `${magnitude}+${significantFigures}+${maxOptions}`
    let serializedConfig = ""

    // 
    if (order[0] instanceof Array) {
        // 
        const separator = separators.shift()

        // 
        for (const innerOrder of /** @type {any} */ (order)) {
            const serializedInnerConfig = serializeConfig(config, innerOrder, {
                magnitude,
                significantFigures,
                maxOptions,
                separators,
                includeDataSizeString: false
            })

            // 
            if (serializedInnerConfig) {
                serializedConfig += serializedConfig
                    ? separator + serializedInnerConfig
                    : serializedInnerConfig
            }
        }
    }
    else {
        // 
        const exponentBits = Math.ceil(
            Math.log2(2 * Math.log2(10 ** magnitude))
        )
        const mantissaBits = Math.ceil(Math.log2(10 ** significantFigures))
        const floatWriteConfig = { signed: false, exponentBits, mantissaBits }

        // 
        const numberSize = exponentBits + mantissaBits
        const optionSize = Math.ceil(Math.log2(maxOptions + 1))
        const serialBuffer = new BitBuffer({
            size: Math.ceil(order.length * Math.max(numberSize, optionSize) / 8)
        })

        for (const entry of /** @type {SerialConfigEntry[]} */ (order)) {
            // 
            let value = /** @type {any} */ (config)
            for (const prop of entry.prop.split(".")) { value = value[prop] }

            // 
            let index = 0
            if (entry.options) { index = entry.options.indexOf(value) + 1 }

            switch (entry.type) {
                case SERIAL_CONFIG_ENTRY_TYPES.boolean:
                    serialBuffer.write(value ? 1 : 0, { size: 1 })
                    break
                case SERIAL_CONFIG_ENTRY_TYPES.integer:
                    serialBuffer.write(value, { size: numberSize })
                    break
                case SERIAL_CONFIG_ENTRY_TYPES.option:
                    serialBuffer.write(index, { size: optionSize })
                    break
                default:
                    serialBuffer.writeFloat(value, floatWriteConfig)
            }
        }

        // 
        serializedConfig = serialBuffer.toString()
    }

    return includeDataSizeString ? `${dataSizeString}+${serializedConfig}`
        : serializedConfig
}

/**
 * 
 * @param {string} serializedConfig 
 * @param {Config} config 
 * @param {SerialConfigOrder} order
 * @param {object} obj
 * @param {number} [obj.magnitude] 
 * @param {number} [obj.significantFigures] 
 * @param {number} [obj.maxOptions]
 * @param {string[]} [obj.separators]
 * @param {boolean} [obj.extractDataSizeString]
 * @param {number[]} [obj.orderPointers]
 * @returns {Config}
 */
const deserializeConfig = (serializedConfig, config, order, {
    magnitude = NaN,
    significantFigures = NaN,
    maxOptions = NaN,
    separators = ["+", "!", "$"],
    extractDataSizeString = true,
    orderPointers = []
} = {}) => {
    // 
    if (extractDataSizeString) {
        const [
            magnitude,
            significantFigures,
            maxOptions,
            ...splitSerializedConfig
        ] = serializedConfig.split("+")

        return deserializeConfig(splitSerializedConfig.join("+"), config, order, {
            magnitude: parseInt(magnitude),
            significantFigures: parseInt(significantFigures),
            maxOptions: parseInt(maxOptions),
            separators,
            extractDataSizeString: false,
            orderPointers
        })
    }

    // 
    const separator = separators.shift()

    if (separator) {
        const innerConfigs = serializedConfig.split(separator)
        for (const [pointer, innerConfig] of innerConfigs.entries()) {
            deserializeConfig(innerConfig, config, order, {
                magnitude,
                significantFigures,
                maxOptions,
                separators: [...separators],
                extractDataSizeString,
                orderPointers: innerConfigs.length === 1
                    ? [...orderPointers]
                    : [...orderPointers, pointer]
            })
        }
    }
    else {
        // 
        const exponentBits = Math.ceil(
            Math.log2(2 * Math.log2(10 ** magnitude))
        )
        const mantissaBits = Math.ceil(Math.log2(10 ** significantFigures))
        const floatReadConfig = { signed: false, exponentBits, mantissaBits }

        // 
        const numberSize = exponentBits + mantissaBits
        const optionSize = Math.ceil(Math.log2(maxOptions + 1))
        const serialBuffer = BitBuffer.from(serializedConfig)

        // 
        let fragment = /** @type {any} */ (order)
        for (const pointer of orderPointers) { fragment = fragment[pointer] }
        while (fragment[0] instanceof Array) { fragment = fragment[0] }

        for (const entry of /** @type {SerialConfigEntry[]} */ (fragment)) {
            let value
            switch (entry.type) {
                case SERIAL_CONFIG_ENTRY_TYPES.boolean:
                    value = serialBuffer.read(1)
                    break
                case SERIAL_CONFIG_ENTRY_TYPES.integer:
                    value = serialBuffer.read(numberSize)
                    break
                case SERIAL_CONFIG_ENTRY_TYPES.option:
                    value = entry.options?.[serialBuffer.read(optionSize) - 1]
                    break
                default:
                    value = parseFloat(
                        serialBuffer.readFloat(floatReadConfig).toPrecision(4)
                    )
            }

            // Defend against NaN or undefined values returned from buffer reads
            // caused by old quotes with missing entries on the end of each
            // config fragment. Note that type checking *before* isNaN check is
            // required to prevent non-number values (such as strings) passing
            // the isNaN check.
            if (
                typeof value === "number" && isNaN(value) ||
                value === undefined
            ) { continue }

            // 
            let target = /** @type {any} */ (config)
            const path = entry.prop.split(".")
            for (const [index, prop] of path.entries()) {
                if (index === path.length - 1) { target[prop] = value }
                else { target = target[prop] }
            }
        }
    }

    return config
}

// @@exports
export { epdmConfig, epdmConfigSerialOrder, serializeConfig, deserializeConfig }
